import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  ColumnLayout,
  Button,
  Container,
  Header,
  Box,
} from "@amzn/awsui-components-react-v3";
import {
  FormSchemaDto,
  FieldGroupDto,
} from "@amzn/crowd-front-end-common-library";
import WizardFooter from "../../wizard/WizardFooter/WizardFooter";
import { useStepResult } from "../../wizard/Wizard/WizardResultContextProvider";
import { MoveTeamWorkflowResult } from "../MoveTeamWorkflowResult";
import { MoveTeamContext } from "../MoveTeamContext";
import { useWizardWorkflowContext } from "../../wizard/Wizard/WizardWorkflowContextProvider";
import { useStepContext } from "../../wizard/Wizard/WizardStepContextProvider";
import { useHttpRequest, LoadingState } from "../../http";
import StaticFieldRenderer from "../../forms/StaticFieldRenderer/StaticFieldRenderer";
import { MoveTeamInputStepResult as InputStepResult } from "../inputStep/MoveTeamInputStepResult";
import moveTeamStyle from "../moveTeam.module.css";
import { MetricsState } from "../../logging";
import StaticTeamRenderer from "../../common/StaticTeamRenderer";
import { HttpStatus } from "../../http/httpStatus";
import { moveTeamAccepted, moveTeamSuccess } from "./MoveTeamBannerMessage";
import {
  teamFirstFailureBanner,
  teamAfterFirstFailureBanner,
} from "../../common/utils/ErrorBannerMessages";
import { BannerComponentId } from "../../banner/bannerComponentId";
import { CustomStepInjectedProps } from "../../wizard/WizardStep/WizardStep";

const PREVIOUS_STEPS = ["Select new parent team"];

const getPreviousStepsEditSection = (
  setStepNumber: React.Dispatch<React.SetStateAction<number>>
): JSX.Element[] => {
  const editSection: JSX.Element[] = PREVIOUS_STEPS.map((step, i) => {
    const onEdit = (): void => {
      setStepNumber(i);
    };

    return (
      <div className={moveTeamStyle["edit-button-section"]}>
        <div>Step {`${i + 1}: ${step}`}</div>
        <Button onClick={onEdit}>Edit</Button>
      </div>
    );
  });
  return editSection;
};

const getTeamInformation = (
  schema: FormSchemaDto,
  input: Partial<InputStepResult>,
  metrics: MetricsState,
  parentTeamId: string,
  parentTeamName: string,
  parentTeamPath: string
): JSX.Element => {
  const groupLabel = "Parent team";
  const teamGroup = schema.groups.find(
    (group) => group.groupLabel === groupLabel
  ) as FieldGroupDto;
  const { fieldDefinitions } = teamGroup;
  const fields = fieldDefinitions.map((fieldDefinition) => (
    <React.Fragment>
      <div>
        <Box variant="awsui-key-label">{fieldDefinition.label}</Box>
        <StaticFieldRenderer
          field={fieldDefinition}
          formValues={input}
          metrics={metrics}
        />
      </div>
    </React.Fragment>
  ));

  return (
    <Container header={<Header>{groupLabel}</Header>}>
      <ColumnLayout>
        <Box variant="awsui-key-label">Current parent team</Box>
        <StaticTeamRenderer
          teamName={parentTeamName}
          teamPath={parentTeamPath}
        />
        <br />
        <div data-awsui-column-layout-root="true">{fields}</div>
      </ColumnLayout>
    </Container>
  );
};

// Returns the name of the new parent that was selected in step 1 of workflow.
const getNewParentName = (
  teamId: string | undefined,
  metrics: MetricsState
): string => {
  const {
    loadingState: targetLoadingState,
    data: response,
    error: targetFetchError,
  } = useHttpRequest("getTeamData", metrics, {
    url: `/api/v1/teams/${teamId}`,
    method: "GET",
  });

  if (targetFetchError) {
    if (targetFetchError.response && targetFetchError.response.status === 404) {
      throw new Error(
        `Unable to resolve team with id: ${teamId}.  Redirecting to home page.`
      );
    } else {
      throw targetFetchError;
    }
  }

  const teamData =
    targetLoadingState === LoadingState.RESOLVED
      ? (response as any).data
      : undefined;

  return !teamData || teamData.name;
};

const MoveTeamReviewStep: React.FC<CustomStepInjectedProps<undefined>> = ({
  goPrevious,
}): JSX.Element => {
  const [input] = useStepResult<MoveTeamWorkflowResult, "input">("input");
  const {
    metrics,
    parentTeamId,
    teamId,
    currentTeam,
    schema,
    setBanner,
    parentTeamName,
    parentTeamPath,
  } = useWizardWorkflowContext<MoveTeamContext>();
  const newParentTeamId = input.newParentTeamId || undefined;
  const newParentName = getNewParentName(newParentTeamId, metrics);
  const [submitTimes, setSubmitTimes] = useState(0);
  const { setStepNumber } = useStepContext();

  const { loadingState, error, sendRequest, response } = useHttpRequest(
    "moveTeam",
    metrics
  );

  const history = useHistory();
  useEffect(() => {
    if (loadingState === LoadingState.RESOLVED) {
      if (response && response.status === HttpStatus.Ok) {
        setBanner(moveTeamSuccess(currentTeam.name, newParentName));
      } else {
        setBanner(moveTeamAccepted(currentTeam.name, newParentName));
      }
      history.push(`/team/${newParentTeamId}`);
    } else if (loadingState === LoadingState.REJECTED) {
      // TODO: Need to handle 404 differently
      if (submitTimes < 2) {
        setBanner(teamFirstFailureBanner("move", "moved"));
      } else {
        setBanner(
          teamAfterFirstFailureBanner(
            "move",
            BannerComponentId.MoveTeamAfterFirstFailure
          )
        );
      }
    } else if (loadingState === LoadingState.LOADING) {
      setBanner(undefined);
    }
  }, [
    loadingState,
    history,
    currentTeam,
    newParentName,
    newParentTeamId,
    setBanner,
    response,
    submitTimes,
  ]);

  const teamInformation = getTeamInformation(
    schema,
    input,
    metrics,
    parentTeamId,
    parentTeamName,
    parentTeamPath
  );

  const previousStepsEditSection = getPreviousStepsEditSection(setStepNumber);

  const onSubmit = (): void => {
    sendRequest({
      url: `/api/v1/teams/${teamId}/move/${newParentTeamId}`,
      method: "PUT",
    });
    setSubmitTimes((times) => times + 1);
  };

  const onPrevious = (): void => {
    setBanner(undefined);
    goPrevious();
  };

  return (
    <React.Fragment>
      <div className={moveTeamStyle["update-step-wrapper"]}>
        {previousStepsEditSection}
        {teamInformation}
        <WizardFooter
          onPrevious={onPrevious}
          onNext={onSubmit}
          nextButtonText="Move team"
          isLoading={loadingState === LoadingState.LOADING}
        />
      </div>
    </React.Fragment>
  );
};

export default MoveTeamReviewStep;
